import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import dynamic from 'next/dynamic'
import {useLinkResolver} from 'prismicio'
import type {HeroV2SliceVideoPrimary} from 'prismicio-types'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {Icon} from 'src/common/Icon'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {IconRatings} from './RatingsSection/IconRatings'
import {StarRatings} from './RatingsSection/StarRatings'

const VideoPlayer = dynamic(() => import('src/common/VideoPlayer').then((mod) => mod.VideoPlayer), {ssr: false})

type VideoHeroProps = {
  slice: {
    primary: HeroV2SliceVideoPrimary
    slice_type: string
    variation: string
  }
  pageUid: string
  componentIndex: number
}

export const VideoHero = ({slice, componentIndex, pageUid}: VideoHeroProps) => {
  const {linkResolver} = useLinkResolver()
  const headlineSection = slice.primary.headlinesection[0]
  const ratingsSection = slice.primary.ratings
  const videoSrc = isFilled.link(slice.primary.video_source) ? slice.primary.video_source.url : ''

  const sanitizedHeading = headlineSection ? sanitizeHtml(headlineSection.heading!) : null
  const useRte = headlineSection && isFilled.richText(headlineSection.heading_rte)
  const centerAlign = !headlineSection?.full_width !== false

  return (
    <Container
      backgroundColor={slice.primary.background_color || 'Moss White'}
      className="!md:py-20 flex flex-col gap-12 !py-10"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {headlineSection && (
        <div className="mx-auto grid w-full max-w-[1200px] scroll-mt-16 grid-cols-4 gap-4 overflow-hidden px-5 md:grid-cols-12 md:gap-x-6 md:px-10 lg:px-5 xl:px-0">
          <div
            className={cn('col-span-4 flex flex-col gap-6', {
              'lg:col-start-4 lg:col-end-10': !headlineSection.extended_text_width && !headlineSection.full_width,
              'lg:col-start-3 lg:col-end-11': headlineSection.extended_text_width && !headlineSection.full_width,
              'md:col-start-2 md:col-end-12': !headlineSection.full_width && centerAlign,
              'md:col-start-1 md:col-end-12': headlineSection.full_width,
            })}
            data-aos="fade-up"
            data-aos-offset="0"
          >
            {isFilled.group(ratingsSection) && (
              <div
                className={cn('flex gap-3', {
                  'justify-center': centerAlign,
                })}
              >
                {ratingsSection &&
                  ratingsSection.map((review, index) =>
                    headlineSection.show_stars_in_rating ? (
                      <StarRatings review={review} key={`${review.review_platform} - ${index}`} />
                    ) : (
                      <IconRatings review={review} key={`${review.review_platform} - ${index}`} />
                    ),
                  )}
              </div>
            )}
            <div
              className={cn('col-span-4 grid grid-cols-4 gap-4 gap-y-6 md:gap-x-6', {
                'text-center': centerAlign,
                'text-start': !centerAlign,
              })}
            >
              {isFilled.richText(headlineSection.eyebrow) && (
                <PrismicRichText
                  field={headlineSection.eyebrow}
                  components={{
                    paragraph: ({children}) => <p className="text-button-s col-span-full text-moss-blue">{children}</p>,
                  }}
                />
              )}
              {useRte ? (
                <div className="col-span-full">
                  <PrismicRichText
                    field={headlineSection.heading_rte}
                    components={{
                      heading1: ({children}) => (
                        <h1 className="text-32 font-medium leading-10 text-moss-black">{children}</h1>
                      ),
                      heading2: ({children}) => (
                        <h2 className="md:text-h2 text-[32px] font-medium leading-[normal] text-moss-black">
                          {children}
                        </h2>
                      ),
                      strong: ({children}) => <strong className="font-medium text-moss-black/60">{children}</strong>,
                    }}
                  />
                </div>
              ) : (
                sanitizedHeading && (
                  <h2
                    className="col-span-full text-32 font-medium leading-10 text-moss-black"
                    dangerouslySetInnerHTML={{__html: sanitizedHeading}}
                  />
                )
              )}
              {isFilled.keyText(headlineSection.subtext) && (
                <p className="col-span-full text-16 leading-6 tracking-[.16px] text-grey-550">
                  {headlineSection.subtext}
                </p>
              )}
            </div>
            {isFilled.keyText(headlineSection.primary_button_label) && (
              <div className="mx-auto mb-6 flex gap-2">
                {isFilled.keyText(headlineSection.primary_button_label) && (
                  <ButtonV2
                    variant="primary"
                    href={linkResolver(headlineSection.primary_button_url, 'primary-button-hero')}
                    trackingData={{
                      mktgId: headlineSection.primary_button_marketing_id || '',
                      location: `component-${componentIndex}-hero`,
                      dataType: 'hero-button',
                      pageName: pageUid,
                      label: headlineSection.primary_button_label,
                    }}
                    id={`component-${componentIndex}-hero-1`}
                  >
                    {headlineSection.primary_button_label}
                  </ButtonV2>
                )}
                {isFilled.keyText(headlineSection.secondary_button_label) && (
                  <ButtonV2
                    href={linkResolver(headlineSection.secondary_button_link, 'secondary-button-hero')}
                    variant="secondary"
                    trackingData={{
                      mktgId: headlineSection.secondary_button_marketing_id || '',
                      location: `component-${componentIndex}-hero`,
                      dataType: 'hero-button',
                      pageName: pageUid,
                      label: headlineSection.secondary_button_label,
                    }}
                    id={`component-${componentIndex}-hero-2`}
                  >
                    {headlineSection.secondary_button_label}
                  </ButtonV2>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div
        className="mx-auto aspect-video w-full max-w-[1200px] px-5 md:px-10 lg:px-5 xl:px-0"
        data-aos="fade-up"
        data-aos-offset="0"
      >
        <VideoPlayer
          src={videoSrc}
          fallbackImg={slice.primary.fallback_image}
          playIcon={
            <div className="text-button-s flex h-12 w-fit items-center gap-2.5 rounded-[4px] bg-moss-white/[30%] p-4 text-moss-white backdrop-blur-[6px] transition-all duration-300 ease-in-out hover:brightness-110 md:self-end lg:!h-[72px] lg:rounded-[6px] lg:p-6 lg:text-18 lg:font-semibold">
              <Icon name="play" className="size-3 text-moss-white lg:h-[22px] lg:w-[22px]" />
              {isFilled.keyText(slice.primary.play_button_text) && slice.primary.play_button_text}
            </div>
          }
          pageUid={pageUid}
          location={`component-${componentIndex}-hero`}
          variation={slice.variation}
          autoplay={slice.primary.autoplay_video}
          startMuted={slice.primary.start_video_muted}
        />
      </div>
    </Container>
  )
}
